import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSystemSettings, updateSystemSettings } from '../../services/api';
import '../../styles/admin-ui.css';
import './Settings.css';

const DEFAULTS = {
  security: {
    password_min_length: 8,
    password_require_uppercase: true,
    password_require_special: true,
    two_factor_required: false,
    session_duration_minutes: 30,
  },
  network: {
    default_protocol: 'wireguard',
    primary_dns: '8.8.8.8',
    secondary_dns: '8.8.4.4',
    udp_port: 51820,
    tcp_port: 443,
  },
  email: {
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from: '',
  },
};

const TABS = [
  { id: 'security', label: 'Security' },
  { id: 'network', label: 'Network' },
  { id: 'email', label: 'Email' },
];

const Settings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('security');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [settings, setSettings] = useState(DEFAULTS);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const data = await getSystemSettings();
      if (!data || typeof data !== 'object') {
        throw new Error('Settings response was empty');
      }
      const smtp = data.notifications || {};
      setSettings({
        security: { ...DEFAULTS.security, ...(data.security || {}) },
        network: { ...DEFAULTS.network, ...(data.network || {}) },
        email: {
          ...DEFAULTS.email,
          smtp_host: smtp.smtp_host ?? DEFAULTS.email.smtp_host,
          smtp_port: Number(smtp.smtp_port ?? DEFAULTS.email.smtp_port) || 587,
          smtp_username: smtp.smtp_username ?? DEFAULTS.email.smtp_username,
          smtp_password: smtp.smtp_password ?? DEFAULTS.email.smtp_password,
          smtp_from: smtp.smtp_from ?? DEFAULTS.email.smtp_from,
        },
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Failed to load settings',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (category, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...(prev[category] || {}),
        [key]: value,
      },
    }));
  };

  const handleSave = async (category) => {
    try {
      setSaving(true);
      const source = settings[category] || {};
      const settingsToUpdate = Object.entries(source).map(([key, value]) => ({
        key,
        value,
      }));
      if (!settingsToUpdate.length) throw new Error('Nothing to save');

      const result = await updateSystemSettings(settingsToUpdate);
      const updated = result?.updated_count;
      setMessage({
        type: 'success',
        text:
          updated != null
            ? `Saved ${updated} setting${updated === 1 ? '' : 's'}`
            : 'Settings saved successfully',
      });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      await fetchSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const security = settings?.security || DEFAULTS.security;
  const network = settings?.network || DEFAULTS.network;
  const email = settings?.email || DEFAULTS.email;

  if (loading) {
    return (
      <div className="aui-page settings-page">
        <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
          ← Back
        </button>
        <div className="aui-empty">
          <div className="aui-spinner" />
          <p>Loading settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="aui-page settings-page">
      <button type="button" className="aui-back" onClick={() => navigate('/dashboard/admin')}>
        ← Back
      </button>

      <div className="st-hero">
        <p className="st-kicker">Control plane</p>
        <h1>System settings</h1>
        <p className="st-hero-lead">
          Security, network defaults, and outbound SMTP — values loaded from the API.
        </p>
      </div>

      {message.text ? <div className={`st-banner ${message.type}`}>{message.text}</div> : null}

      <div className="st-tabs" role="tablist">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`st-tab ${activeTab === tab.id ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="st-content">
        {activeTab === 'security' && (
          <div className="st-block-wrap">
            <h2>Security</h2>
            <p className="st-help">Password policy, 2FA requirement, and session lifetime.</p>

            <label className="st-field">
              <span>Minimum password length</span>
              <input
                type="number"
                value={security.password_min_length ?? 8}
                onChange={(e) =>
                  handleSettingChange('security', 'password_min_length', parseInt(e.target.value, 10) || 0)
                }
              />
            </label>

            <label className="st-check">
              <input
                type="checkbox"
                checked={!!security.password_require_uppercase}
                onChange={(e) =>
                  handleSettingChange('security', 'password_require_uppercase', e.target.checked)
                }
              />
              <span>Require uppercase</span>
            </label>

            <label className="st-check">
              <input
                type="checkbox"
                checked={!!security.password_require_special}
                onChange={(e) =>
                  handleSettingChange('security', 'password_require_special', e.target.checked)
                }
              />
              <span>Require special character</span>
            </label>

            <label className="st-check">
              <input
                type="checkbox"
                checked={!!security.two_factor_required}
                onChange={(e) =>
                  handleSettingChange('security', 'two_factor_required', e.target.checked)
                }
              />
              <span>2FA required for all users</span>
            </label>

            <label className="st-field">
              <span>Session duration (minutes)</span>
              <input
                type="number"
                value={security.session_duration_minutes ?? 30}
                onChange={(e) =>
                  handleSettingChange(
                    'security',
                    'session_duration_minutes',
                    parseInt(e.target.value, 10) || 0
                  )
                }
              />
            </label>

            <div className="st-actions">
              <button
                type="button"
                className="st-save"
                onClick={() => handleSave('security')}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save security'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'network' && (
          <div className="st-block-wrap">
            <h2>Network</h2>
            <p className="st-help">Defaults pushed to agents via websocket network settings.</p>

            <label className="st-field">
              <span>Default protocol</span>
              <select
                value={network.default_protocol || 'wireguard'}
                onChange={(e) => handleSettingChange('network', 'default_protocol', e.target.value)}
              >
                <option value="wireguard">WireGuard</option>
                <option value="openvpn">OpenVPN</option>
                <option value="ikev2">IKEv2</option>
              </select>
            </label>

            <label className="st-field">
              <span>Primary DNS</span>
              <input
                type="text"
                value={network.primary_dns || ''}
                onChange={(e) => handleSettingChange('network', 'primary_dns', e.target.value)}
              />
            </label>

            <label className="st-field">
              <span>Secondary DNS</span>
              <input
                type="text"
                value={network.secondary_dns || ''}
                onChange={(e) => handleSettingChange('network', 'secondary_dns', e.target.value)}
              />
            </label>

            <div className="st-row">
              <label className="st-field">
                <span>UDP port</span>
                <input
                  type="number"
                  value={network.udp_port ?? 51820}
                  onChange={(e) =>
                    handleSettingChange('network', 'udp_port', parseInt(e.target.value, 10) || 0)
                  }
                />
              </label>
              <label className="st-field">
                <span>TCP port</span>
                <input
                  type="number"
                  value={network.tcp_port ?? 443}
                  onChange={(e) =>
                    handleSettingChange('network', 'tcp_port', parseInt(e.target.value, 10) || 0)
                  }
                />
              </label>
            </div>

            <div className="st-actions">
              <button
                type="button"
                className="st-save"
                onClick={() => handleSave('network')}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save network'}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'email' && (
          <div className="st-block-wrap">
            <h2>Email / SMTP</h2>
            <p className="st-help">
              Used when environment SMTP variables are empty. Env vars still take priority when set.
            </p>

            <label className="st-field">
              <span>SMTP host</span>
              <input
                type="text"
                placeholder="smtp.example.com"
                value={email.smtp_host || ''}
                onChange={(e) => handleSettingChange('email', 'smtp_host', e.target.value)}
              />
            </label>

            <label className="st-field">
              <span>SMTP port</span>
              <input
                type="number"
                value={email.smtp_port ?? 587}
                onChange={(e) =>
                  handleSettingChange('email', 'smtp_port', parseInt(e.target.value, 10) || 0)
                }
              />
            </label>

            <label className="st-field">
              <span>Username</span>
              <input
                type="text"
                value={email.smtp_username || ''}
                onChange={(e) => handleSettingChange('email', 'smtp_username', e.target.value)}
              />
            </label>

            <label className="st-field">
              <span>Password</span>
              <input
                type="password"
                value={email.smtp_password || ''}
                onChange={(e) => handleSettingChange('email', 'smtp_password', e.target.value)}
              />
            </label>

            <label className="st-field">
              <span>From address</span>
              <input
                type="email"
                placeholder="noreply@example.com"
                value={email.smtp_from || ''}
                onChange={(e) => handleSettingChange('email', 'smtp_from', e.target.value)}
              />
            </label>

            <div className="st-actions">
              <button
                type="button"
                className="st-save"
                onClick={() => handleSave('email')}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save email'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
